import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { todayInWorkspaceTz } from "@/hooks/useWorkspaceTasks";
import { getTaskUrgency } from "@/lib/task-urgency";
import { toLocalDateISO, periodToMetricDates, computeWorkspaceTaskMetrics } from "@/lib/task-metrics";

const STALE_MS = 60_000;
const REALTIME_DEBOUNCE_MS = 3000;

export interface TaskDashboardCounts {
  overdue: number;
  dueToday: number;
  approaching: number;
  completedToday: number;
  pendingActionable: number;
  filterToday: number;
  filterOverdue: number;
  filterUpcoming: number;
}

export interface TaskDayProgress {
  done: number;
  total: number;
  percent: number;
}

export interface TaskDashboardSnapshot {
  counts: TaskDashboardCounts;
  dayProgress: TaskDayProgress;
  weekProgress: { percent: number };
  motivationalLabel: string;
  hasUrgentAttention: boolean;
  pendingTotal: number;
  showMissionStrip: boolean;
  loading: boolean;
  refetch: () => Promise<void>;
}

const EMPTY_COUNTS: TaskDashboardCounts = {
  overdue: 0,
  dueToday: 0,
  approaching: 0,
  completedToday: 0,
  pendingActionable: 0,
  filterToday: 0,
  filterOverdue: 0,
  filterUpcoming: 0,
};

function addDaysISO(dateIso: string, days: number): string {
  const d = new Date(dateIso + "T12:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function motivationalLabelFor(percent: number, overdue: number): string {
  if (overdue > 0) return "Resolva as atrasadas primeiro";
  if (percent >= 100) return "Dia concluído — ótimo trabalho!";
  if (percent >= 50) return "No caminho — continue assim";
  if (percent > 0) return "Bom começo — siga na missão";
  return "Comece sua missão de hoje";
}

function computeSnapshot(
  rows: Array<{
    due_date: string | null;
    due_at: string | null;
    status: string;
    completed_at: string | null;
    outcome: string | null;
    assigned_member_id: string;
  }>,
  memberId: string,
  timezone: string,
): Omit<TaskDashboardSnapshot, "loading" | "refetch"> {
  const today = todayInWorkspaceTz(timezone);

  const mine = rows.filter((r) => r.assigned_member_id === memberId);

  let overdue = 0;
  let dueToday = 0;
  let approaching = 0;
  let completedToday = 0;
  let pendingActionable = 0;
  let filterToday = 0;
  let filterOverdue = 0;
  let filterUpcoming = 0;

  for (const t of mine) {
    if (t.status === "cancelled") continue;

    if (t.status === "completed") {
      const completedLocal = t.completed_at
        ? toLocalDateISO(t.completed_at, timezone)
        : null;
      if (completedLocal === today) completedToday += 1;
      continue;
    }

    if (t.status !== "pending") continue;

    if (!t.due_date) continue;

    const urgency = getTaskUrgency(
      { status: "pending", due_date: t.due_date, due_at: t.due_at },
      timezone,
    );

    if (urgency.level === "overdue") {
      overdue += 1;
      filterOverdue += 1;
    } else if (urgency.level === "today") {
      dueToday += 1;
      filterToday += 1;
    } else if (urgency.level === "approaching") {
      approaching += 1;
    }

    if (t.due_date > today) {
      filterUpcoming += 1;
    }

    if (t.due_date <= today || urgency.level === "overdue") {
      pendingActionable += 1;
    }
  }

  const dayTotal = pendingActionable + completedToday;
  const dayDone = completedToday;
  const percent = dayTotal > 0 ? Math.min(100, Math.round((dayDone / dayTotal) * 100)) : 0;

  const { from, to } = periodToMetricDates("7d", timezone);
  const weekMetrics = computeWorkspaceTaskMetrics(
    [{ id: memberId, display_name: null }],
    mine.map((t) => ({
      assigned_member_id: t.assigned_member_id,
      status: t.status as "pending" | "completed" | "cancelled",
      outcome: t.outcome as "positive" | "negative" | null,
      due_date: t.due_date,
      completed_at: t.completed_at,
      updated_at: t.completed_at ?? new Date().toISOString(),
    })),
    from,
    to,
    timezone,
  );
  const weekPercent = weekMetrics[0]?.completion_rate ?? 0;

  const counts: TaskDashboardCounts = {
    overdue,
    dueToday,
    approaching,
    completedToday,
    pendingActionable,
    filterToday,
    filterOverdue,
    filterUpcoming,
  };

  return {
    counts,
    dayProgress: { done: dayDone, total: dayTotal, percent },
    weekProgress: { percent: weekPercent },
    motivationalLabel: motivationalLabelFor(percent, overdue),
    hasUrgentAttention: overdue > 0,
    pendingTotal: pendingActionable,
    showMissionStrip: dayTotal > 0 || overdue > 0 || approaching > 0,
  };
}

const DISABLED_SNAPSHOT: TaskDashboardSnapshot = {
  counts: EMPTY_COUNTS,
  dayProgress: { done: 0, total: 0, percent: 0 },
  weekProgress: { percent: 0 },
  motivationalLabel: "",
  hasUrgentAttention: false,
  pendingTotal: 0,
  showMissionStrip: false,
  loading: false,
  refetch: async () => {},
};

export function useMyTaskDashboard(
  workspaceId: string | null,
  memberId: string | null,
  disabled = false,
): TaskDashboardSnapshot {
  const [snapshot, setSnapshot] = useState(DISABLED_SNAPSHOT);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastFetchRef = useRef(0);
  const timezoneRef = useRef("America/Sao_Paulo");
  const baseTitleRef = useRef(typeof document !== "undefined" ? document.title : "");

  const fetchDashboard = useCallback(async () => {
    if (disabled || !workspaceId || !memberId) {
      setSnapshot(DISABLED_SNAPSHOT);
      return;
    }

    lastFetchRef.current = Date.now();
    setLoading(true);
    try {
      const { data: workspace } = await supabase
        .from("workspaces")
        .select("timezone")
        .eq("id", workspaceId)
        .maybeSingle();

      const timezone = workspace?.timezone ?? "America/Sao_Paulo";
      timezoneRef.current = timezone;
      const today = todayInWorkspaceTz(timezone);
      const horizon = addDaysISO(today, 3);
      const completedSinceDate = addDaysISO(today, -7);

      // Inclui tarefas-raiz e subtarefas atribuídas ao membro: ambas contam
      // como trabalho do dia na missão pessoal.
      const { data, error } = await supabase
        .from("workspace_tasks")
        .select("due_date, due_at, status, completed_at, outcome, assigned_member_id")
        .eq("workspace_id", workspaceId)
        .eq("assigned_member_id", memberId)
        .neq("status", "cancelled");

      if (error) throw error;

      const filtered = (data ?? []).filter((row) => {
        if (row.status === "pending") {
          if (!row.due_date) return true;
          return row.due_date <= horizon;
        }
        if (row.status === "completed" && row.completed_at) {
          return toLocalDateISO(row.completed_at, timezone) >= completedSinceDate;
        }
        return false;
      });

      const computed = computeSnapshot(
        filtered as Array<{
          due_date: string | null;
          due_at: string | null;
          status: string;
          completed_at: string | null;
          outcome: string | null;
          assigned_member_id: string;
        }>,
        memberId,
        timezone,
      );

      setSnapshot({
        ...computed,
        loading: false,
        refetch: async () => {},
      });
    } catch (err) {
      console.error("useMyTaskDashboard:", err);
    } finally {
      setLoading(false);
    }
  }, [workspaceId, memberId, disabled]);

  useEffect(() => {
    void fetchDashboard();
  }, [fetchDashboard]);

  useEffect(() => {
    if (disabled || !workspaceId) return;

    const onTasksChanged = () => {
      if (Date.now() - lastFetchRef.current < 800) return;
      void fetchDashboard();
    };
    window.addEventListener("fabchat-tasks-changed", onTasksChanged);

    const interval = setInterval(() => {
      if (
        document.visibilityState === "visible" &&
        Date.now() - lastFetchRef.current >= STALE_MS
      ) {
        void fetchDashboard();
      }
    }, STALE_MS);

    return () => {
      window.removeEventListener("fabchat-tasks-changed", onTasksChanged);
      clearInterval(interval);
    };
  }, [workspaceId, disabled, fetchDashboard]);

  const fetchDashboardRef = useRef(fetchDashboard);
  fetchDashboardRef.current = fetchDashboard;

  useEffect(() => {
    if (disabled || !workspaceId) return;

    const channelName = `task-dashboard-${workspaceId}-${memberId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "workspace_tasks",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          void fetchDashboardRef.current();
        }, REALTIME_DEBOUNCE_MS);
      },
    );

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId, memberId, disabled]);

  useEffect(() => {
    if (typeof document === "undefined" || disabled) return;
    if (!baseTitleRef.current) {
      baseTitleRef.current = document.title.replace(/^\(\d+!\)\s*/, "");
    }
    const base = baseTitleRef.current;
    if (snapshot.hasUrgentAttention) {
      document.title = `(${snapshot.counts.overdue}!) ${base}`;
    } else {
      document.title = base;
    }
    return () => {
      document.title = base;
    };
  }, [snapshot.hasUrgentAttention, snapshot.counts.overdue, disabled]);

  return {
    ...snapshot,
    loading,
    refetch: fetchDashboard,
  };
}
